import { cn } from '@/lib/utils';
import {
  testCasesCardClass,
  testCasesCardChildClass,
  testCasesMainClass,
  testCasesPageClass,
  testCasesScriptsHeaderClass,
  testCasesScriptsListClass,
  testCasesScriptsScrollClass,
  testCasesTabRootClass,
  testCasesTreeWrapClass,
} from './test-cases-layout';

const SkeletonBar = ({ className }: { className?: string }) => (
  <div
    className={cn(
      'animate-pulse rounded-md bg-[var(--inv-surface-muted)]',
      className,
    )}
  />
);

const ScriptCardSkeleton = () => (
  <div className="mb-3 flex flex-wrap items-center gap-4 rounded-[var(--inv-radius-control,12px)] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-4 py-3.5">
    <SkeletonBar className="h-10 w-10 shrink-0 rounded-lg" />
    <div className="min-w-[220px] flex-1">
      <SkeletonBar className="mb-2 h-4 w-[min(100%,14rem)]" />
      <SkeletonBar className="mb-2.5 h-3 w-[min(100%,10rem)]" />
      <SkeletonBar className="h-1.5 w-full rounded-full" />
      <SkeletonBar className="mt-1.5 h-2.5 w-24" />
    </div>
    <div className="min-w-[4.5rem] shrink-0 text-center">
      <SkeletonBar className="mx-auto h-7 w-12" />
      <SkeletonBar className="mx-auto mt-1 h-2 w-14" />
    </div>
    <SkeletonBar className="h-6 w-16 shrink-0 rounded-full" />
  </div>
);

export const TestCasesEntitiesSkeleton = () => (
  <ul className="m-0 grid list-none grid-cols-1 gap-2 p-0 sm:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: 6 }).map((_, index) => (
      <li
        key={index}
        className="flex flex-col gap-1.5 rounded-[var(--inv-radius-control,12px)] border border-solid border-[var(--inv-border)] bg-[var(--inv-bg)] px-3 py-2.5">
        <SkeletonBar className="h-3.5 w-[min(100%,8rem)]" />
        <SkeletonBar className="h-2.5 w-12" />
        <SkeletonBar className="h-2.5 w-20" />
      </li>
    ))}
  </ul>
);

export const TestCasesTabSkeleton = () => (
  <div
    className={cn(testCasesTabRootClass, 'animate-pulse')}
    aria-busy="true"
    aria-label="Loading test cases">
    <div className={testCasesMainClass}>
      <div className={testCasesTreeWrapClass}>
        <div className={testCasesScriptsHeaderClass}>
          <SkeletonBar className="h-4 w-40" />
          <div className="flex flex-wrap items-center gap-3">
            <SkeletonBar className="h-9 w-28 rounded-[10px]" />
            <SkeletonBar className="h-9 w-[7.5rem] rounded-[10px]" />
            <SkeletonBar className="h-4 w-24" />
            <SkeletonBar className="h-9 w-[4.5rem] rounded-[10px]" />
          </div>
        </div>

        <div className={testCasesScriptsScrollClass}>
          <div className={testCasesScriptsListClass}>
            {Array.from({ length: 6 }).map((_, index) => (
              <ScriptCardSkeleton key={index} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const TestCasesSkeleton = () => (
  <div
    className={cn(testCasesPageClass, 'animate-pulse')}
    aria-busy="true"
    aria-label="Loading test cases">
    {/* Page header */}
    <header className="mb-5 max-w-[42rem]">
      <SkeletonBar className="mb-3 h-2.5 w-24" />
      <SkeletonBar className="mb-2 h-8 w-[min(100%,12rem)]" />
      <SkeletonBar className="h-4 w-[min(100%,32rem)]" />
    </header>

    {/* Project context */}
    <SkeletonBar className="mb-4 h-11 w-full rounded-xl" />

    <section className={testCasesCardClass} aria-hidden="true">
      <div className={testCasesCardChildClass}>
        <TestCasesTabSkeleton />
      </div>
    </section>
  </div>
);

export default TestCasesSkeleton;
